<script setup lang="ts">
import type { MarketingFlowStep } from '../types/marketingFlow'

const { t } = useI18n()

withDefaults(
  defineProps<{
    type: MarketingFlowStep
    selected?: boolean
    disabled?: boolean
    statsCount?: number | null
    statsLabel?: string | null
    isStatsLoaded?: boolean | null
    errorStatus?: 'invalid' | 'incomplete' | null
  }>(),
  {
    selected: false,
    statsCount: null,
    statsLabel: null,
    isStatsLoaded: false,
    errorStatus: null
  }
)
</script>

<template>
  <div
    class="transition-border flex w-[425px] max-w-full flex-col rounded-xl border bg-white duration-150"
    :class="[
      {
        'cursor-pointer border-red-300 hover:border-red-400': errorStatus === 'invalid' && !selected && !disabled,
        'cursor-pointer border-yellow-400/25 hover:border-yellow-400/50':
          errorStatus === 'incomplete' && !selected && !disabled,
        'border-yellow-400': errorStatus === 'incomplete' && selected,
        'border-red-600': errorStatus === 'invalid' && selected,
        'border-primary': selected && errorStatus === null,
        'cursor-pointer hover:border-primary/15': !disabled && errorStatus === null && !selected,
        'overflow-hidden': statsCount !== null
      }
    ]"
  >
    <div class="flex flex-row items-center p-3.5 pb-0">
      <div
        class="flex h-8 w-8 flex-row items-center justify-center rounded-lg"
        :class="{
          'bg-yellow-50': type === 'trigger',
          'bg-green-50': type === 'delay',
          'bg-indigo-50': type === 'action'
        }"
      >
        <BaseIcon v-if="type === 'trigger'" name="HeroBoltIconSolid" class="h-5 w-5 text-yellow-400" />
        <BaseIcon v-if="type === 'delay'" name="HeroClockIconOutline" class="h-5 w-5 text-green-400" />
        <BaseIcon v-if="type === 'action'" name="HeroCog6ToothIconOutline" class="h-5 w-5 text-indigo-400" />
      </div>

      <span v-if="type === 'trigger'" class="ml-3 text-sm text-foreground">
        {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.title') }}
      </span>
      <span v-if="type === 'delay'" class="ml-3 text-sm text-foreground">
        {{ t('pages.marketingFlow.marketingFlowEditPage.delay.title') }}
      </span>
      <span v-if="type === 'action'" class="ml-3 text-sm text-foreground">
        {{ t('pages.marketingFlow.marketingFlowEditPage.action.title') }}
      </span>
    </div>

    <div class="p-3.5 pt-0">
      <slot />
    </div>

    <div v-if="statsCount !== null" class="border-border flex flex-col border-t bg-muted px-4 py-3 text-xs">
      <span class="font-light text-foreground-secondary"> {{ statsLabel }} </span>
      <span v-if="isStatsLoaded" class="mt-1 font-bold text-foreground">
        {{ statsCount }}
      </span>
      <div v-else class="mt-1 flex h-4 items-center">
        <div class="h-2 w-6 animate-pulse rounded-md bg-stone-300"></div>
      </div>
    </div>
  </div>
</template>
